import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import { data } from '../data';

const Kontakt = () => {
  const [status, setStatus] = useState(null); // 'loading', 'success', 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    const formData = new FormData(e.target);
    const payload = Object.fromEntries(formData.entries());
    
    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="pt-48 pb-20 bg-stone-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-light text-[#081f32] mb-6 uppercase tracking-wider"
          >
            Kontakt Os
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto font-light"
          >
            Vi sidder klar til at hjælpe dig med at planlægge dit næste arrangement. Ræk ud til os for et uforpligtende tilbud eller en rundvisning.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100"
            >
              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#081f32]/5 flex items-center justify-center text-[#081f32] shrink-0">
                    <Phone size={24} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Telefon</p>
                    <a href={`tel:${data.contact.phone}`} className="text-xl font-medium text-[#081f32] hover:text-[#dab256] transition-colors">
                      {data.contact.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#081f32]/5 flex items-center justify-center text-[#081f32] shrink-0">
                    <Mail size={24} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">E-mail</p>
                    <a href={`mailto:${data.contact.email}`} className="text-xl font-medium text-[#081f32] hover:text-[#dab256] transition-colors break-all">
                      {data.contact.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#081f32]/5 flex items-center justify-center text-[#081f32] shrink-0">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Adresse</p>
                    <p className="text-lg text-gray-700 leading-snug">
                      {data.contact.address}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#081f32]/5 flex items-center justify-center text-[#081f32] shrink-0">
                    <Clock size={24} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Åbningstider</p>
                    <p className="text-lg text-gray-700">
                      {data.contact.openingHours}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-[#081f32] p-8 rounded-3xl shadow-xl text-white relative overflow-hidden"
            >
              <div className="relative z-10">
                <h3 className="text-2xl font-light mb-4 tracking-wider uppercase">Besøg Os</h3>
                <p className="text-stone-300 font-light mb-6">
                  Vi viser altid gerne vores smukke lokaler frem. Kontakt os for at aftale en personlig rundvisning.
                </p>
                <div className="h-40 rounded-2xl bg-white/10 flex items-center justify-center border border-white/10">
                   <MapPin className="text-white/20" size={64} />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Form Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-2 bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-gray-100"
          >
            {status === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle2 size={40} />
                </div>
                <h2 className="text-3xl font-light text-[#081f32] mb-4 uppercase tracking-wider">Tak for din besked!</h2>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                  Vi har modtaget din henvendelse og vender tilbage til dig så hurtigt som muligt – typisk inden for 24 timer på hverdage.
                </p>
                <button 
                  onClick={() => setStatus(null)}
                  className="text-[#dab256] uppercase tracking-widest text-sm font-semibold hover:underline"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-3xl font-light text-[#081f32] mb-8 uppercase tracking-wider">Send os en forespørgsel</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2 uppercase tracking-widest">Navn</label>
                      <input 
                        required 
                        name="name" 
                        type="text" 
                        placeholder="Dit fulde navn"
                        className="w-full px-5 py-4 rounded-xl border border-gray-200 bg-stone-50 text-[#081f32] focus:ring-2 focus:ring-[#dab256] focus:border-transparent outline-none transition-all placeholder:text-gray-300" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2 uppercase tracking-widest">E-mail</label>
                      <input 
                        required 
                        name="email" 
                        type="email" 
                        placeholder="din@email.dk"
                        className="w-full px-5 py-4 rounded-xl border border-gray-200 bg-stone-50 text-[#081f32] focus:ring-2 focus:ring-[#dab256] focus:border-transparent outline-none transition-all placeholder:text-gray-300" 
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2 uppercase tracking-widest">Telefon</label>
                      <input 
                        required 
                        name="phone" 
                        type="tel" 
                        placeholder="+45"
                        className="w-full px-5 py-4 rounded-xl border border-gray-200 bg-stone-50 text-[#081f32] focus:ring-2 focus:ring-[#dab256] focus:border-transparent outline-none transition-all placeholder:text-gray-300" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2 uppercase tracking-widest">Type arrangement</label>
                      <select 
                        name="type" 
                        className="w-full px-5 py-4 rounded-xl border border-gray-200 bg-stone-50 text-[#081f32] focus:ring-2 focus:ring-[#dab256] focus:border-transparent outline-none transition-all"
                      >
                        <option value="konference">Konference / Erhverv</option>
                        <option value="bryllup">Bryllup</option>
                        <option value="konfirmation">Konfirmation</option>
                        <option value="mindehoejtidelighed">Mindehøjtidelighed</option>
                        <option value="andet">Andet</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-2 uppercase tracking-widest">Besked</label>
                    <textarea 
                      required 
                      name="message" 
                      rows="5" 
                      placeholder="Fortæl os lidt om jeres ønsker, dato og antal gæster..."
                      className="w-full px-5 py-4 rounded-xl border border-gray-200 bg-stone-50 text-[#081f32] focus:ring-2 focus:ring-[#dab256] focus:border-transparent outline-none transition-all placeholder:text-gray-300"
                    ></textarea>
                  </div>

                  {/* Honeypot field (Antispam) */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <input type="text" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  {status === 'error' && (
                    <div className="p-4 bg-red-50 text-red-600 rounded-xl text-sm">
                      Der skete en fejl under afsendelsen. Prøv venligst igen eller kontakt os direkte via telefon.
                    </div>
                  )}

                  <button 
                    disabled={status === 'loading'}
                    type="submit" 
                    className="w-full bg-[#081f32] text-white py-5 rounded-xl text-sm font-light uppercase tracking-[0.2em] hover:bg-[#0d2a45] transition-all shadow-lg flex items-center justify-center gap-3 disabled:opacity-50"
                  >
                    {status === 'loading' ? 'Sender...' : (
                      <>
                        Send Forespørgsel <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Kontakt;
